'use client'
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect
} from 'react'
import { getFromStorage, saveToStorage } from '../helpers/storage.helper'
import { getInitialSetting } from '../helpers/getInitialSetting'

const ThemeContext = createContext()

export const themeColors = {
  light: {
    brandPrimaryColor: '#5e239d',
    brandPrimaryColorTransp: 'rgba(94, 35, 157,.2)',
    brandSecondaryColor: '#05bbaa',
    brandSecondaryColorTransp: 'rgba(5, 187, 170,.2)',
    brandRed: '#EB5951',
    brandYellow: '#fdebb0',
    textOnImage: '#fff',
    slightBlack: 'rgba(0,0,0,.3)',
    contrast0: '#fff',
    contrast25: '#eeebf2',
    contrast50: '#cdcdcd',
    contrast75: 'grey',
    contrast100: '#2a2a2a'
  },
  dark: {
    brandPrimaryColor: '#5e239d',
    brandPrimaryColorTransp: 'rgba(94, 35, 157,.2)',
    brandSecondaryColor: '#05bbaa',
    brandSecondaryColorTransp: 'rgba(5, 187, 170,.2)',
    brandRed: '#EB5951',
    brandYellow: '#fdebb0',
    textOnImage: '#fff',
    slightBlack: 'rgba(0,0,0,.3)',
    contrast0: '#000',
    contrast25: '#121212',
    contrast50: '#363636',
    contrast75: 'grey',
    contrast100: '#eeebf2'
  }
}

export function useTheme () {
  return useContext(ThemeContext)
}

export default function ThemeProvider ({ children }) {
  const [theme, setThemeState] = useState('light')
  const colors = themeColors[theme]
  const [loadingTheme, setloadingTheme] = useState(true)

  useEffect(() => {
    const val = getInitialSetting({
      key: 'theme',
      urlParam: 'theme',
      allowed: ['light', 'dark'],
      fallback: () =>
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
    })
    setThemeState(val || 'light')
    setloadingTheme(false)
  }, [])

  useEffect(() => {
    if (!loadingTheme) saveToStorage('theme', theme)
  }, [theme, loadingTheme])

  // Set CSS variables for background/foreground on theme change
  useEffect(() => {
    if (!loadingTheme) {
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty(
          '--background',
          colors.contrast0
        )
        document.documentElement.style.setProperty(
          '--foreground',
          colors.contrast100
        )
      }
    }
  }, [theme, colors?.contrast100, colors?.contrast0, loadingTheme])

  const setTheme = newTheme => {
    setThemeState(newTheme)
  }

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      colors
    }),
    [theme, colors]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
